import React from 'react';
import { Modal, Button, Table, Tag, Typography, Row, Col, notification } from 'antd';
import { CheckCircleOutlined, EuroCircleOutlined, CopyOutlined } from '@ant-design/icons';
import '../../css/poolDetailsModal.css';
import cbgreenIcon from "../../assets/icons/pooling/cb-green.svg"; 
import cbredIcon from "../../assets/icons/pooling/cb-red.svg"; 
import euroCurrencyIcon from "../../assets/icons/pooling/euroCurrency.svg"; 
import { useSelector } from 'react-redux';
import ClipboardJS from 'clipboard'; 

const addThousandSeparator = (value) => {
  // Ensure the value is a valid number
  const number = parseFloat(value);

  if (isNaN(number)) {
    return ''; // Return an empty string for invalid input
  }

  // Format the number with thousand separators
  return number.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 20, // Adjust to retain maximum precision if needed
  });
};


const { Text } = Typography;

const PoolDetailsModal = ({ isVisible, onClose }) => {
  const { intiatedpoolingData } = useSelector((state) => state.pooling);
  console.log("poolDetailsData", intiatedpoolingData);

  const surplusCount = intiatedpoolingData.surplus.length;
  const deficitCount = intiatedpoolingData.deficit.length;
  const { user } = useSelector((state) => state.user);
  

  // Copy to Clipboard function
  const copyToClipboard = (text) => {
      // Create a temporary button for Clipboard.js to bind
      const tempButton = document.createElement('button');
      tempButton.style.opacity = '0'; // Hide the button
      tempButton.style.position = 'fixed'; // Prevent UI shift
      document.body.appendChild(tempButton);
    
      // Initialize Clipboard.js
      const clipboard = new ClipboardJS(tempButton, {
        text: () => text,
      });
    
      clipboard.on('success', () => {
        notification.success({
          message: 'Copied',
          description: 'The content has been copied to clipboard.',
        });
        clipboard.destroy();
        document.body.removeChild(tempButton);
      });
    
      clipboard.on('error', () => {
        notification.error({
          message: 'Copy failed',
          description: 'Failed to copy the content.',
        });
        clipboard.destroy();
        document.body.removeChild(tempButton);
      });
    
      // Programmatically trigger a click to activate Clipboard.js
      tempButton.click();
    };
  

  const columns = [
    {
      title: <span style={{ fontWeight: '400' }}>Token ID</span>,
      dataIndex: 'tokenId',
      key: 'tokenId',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {text}
          <button
            style={{ 
              border: 'none', 
              background: 'none', 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center' 
            }}
            onClick={() => copyToClipboard(text)}
          >
            <CopyOutlined style={{ marginLeft: 8 }} />
          </button>
        </div>
      ),
    },
    {
      title: <span style={{ fontWeight: '400' }}>Vessel Name</span>,
      dataIndex: 'vesselName',
      key: 'vesselName',
      width: 130,
      render: (text, record) => (record.is_my_vessel ? text : <div style={{display:"flex",justifyContent:"center",alignItems:"center"}}>-</div>), // Show only if is_my_vessel is true
    },
    ...(user.role === "Admin Pooling Agent" ? [
      {
        title: <span style={{ fontWeight: '400' }}>Brokerage (%)</span>,
        dataIndex: 'broker_percentage',
        key: 'broker_percentage',
        align: 'center',
        width: 100,
      },
    ] : []),
    {
      title: <span style={{ fontWeight: '400' }}>C.B. <br/>(MT CO<sub>2</sub>e)</span>,
      dataIndex: 'complianceBalance',
      key: 'complianceBalance',
      align: 'center',
      width: 155,
      render: (balance) => (
        <Text style={{ display:'flex', 
        // justifyContent:'center',
        color: balance >= 0 ? '#0CC876' : '#c80c0c' }}>
          {balance >= 0 ? (
            <div className='flex' style={{ display:"flex",alignItems:"center",justifyContent:"left" }}>
              {/* <img src={cbgreenIcon} alt="CB green" /> */}
              {addThousandSeparator(Math.abs(balance))}
            </div>
          ) : (
            <div className='flex' style={{ display:"flex",alignItems:"center",justifyContent:"left" }}>
              {/* <img src={cbredIcon} alt="CB red" /> */}
              {addThousandSeparator(Math.abs(balance))} 
            </div>
          )}
        </Text>
      ),      
    },
    {
      title: <span style={{ fontWeight: '400' }}>Compensation (€) Settled At <span style={{color:"#678DFF"}}>{intiatedpoolingData?.settled_price?.toFixed(4)}</span><br/>(€/MT CO<sub>2</sub>e)</span>,
      dataIndex: 'settledAt',
      key: 'settledAt',
      align: 'center',
      width: 130,
      render: (balance) => (
        <div style={{ display:'flex',
        justifyContent:'center',
        color: balance >= 0 ? '#0CC876' : '#c80c0c' }}>
          {balance >= 0 ? (
            <div style={{ display:"flex",alignItems:"center",justifyContent:"center" }}>
              {addThousandSeparator(Math.abs(balance))} 
            </div>
          ) : (
            <div style={{ display:"flex",alignItems:"center",justifyContent:"center" }}>
              {addThousandSeparator(Math.abs(balance))} 
            </div>
          )}
        </div>
      ),    
    }
    // ,
    // {
    //   title: <span style={{ fontWeight: '400' }}>Savings (€)</span>,
    //   dataIndex: 'savings',
    //   key: 'savings',
    //   align: 'center',
    //   width: 130,
    //   render: (balance) => (
    //     <div style={{ display:'flex',
    //     justifyContent:'center',
    //     color: '#678DFF' }}>
    //      {addThousandSeparator(balance)}
    //     </div>
    //   ),    
    // }    
  ];

  // Data source - combining surplus and deficit data
  const dataSource = [
    ...intiatedpoolingData.surplus.map((item, index) => ({
      key: `surplus-${index}`,
      tokenId: item.token_id,
      vesselName: item.vessel_name,
      complianceBalance: (Math.floor(item.compliance_balance / 10**6 * 1000000) / 1000000).toFixed(6),
      broker_percentage: item.broker_percentage,
      settledAt: (Math.floor(item.amount / 10**6 * 10000) / 10000).toFixed(4), // Assuming this represents the settled price
      // savings: (Math.floor(item.penalty * 10000) / 10000).toFixed(4),
      surplusDeficit: 'surplus', // Added to identify surplus items
      is_my_vessel: item?.is_my_vessel, 
    })),
    ...intiatedpoolingData.deficit.map((item, index) => ({
      key: `deficit-${index}`,
      tokenId: item.token_id,
      vesselName: item.vessel_name,
      complianceBalance: (Math.floor(item.compliance_balance / 10**6 * 1000000) / 1000000).toFixed(6),
      broker_percentage: item.broker_percentage,
      settledAt: (Math.floor(item.amount / 10**6 * 10000) / 10000).toFixed(4), // Assuming this represents the settled price
      // savings: (Math.floor((parseFloat(item.amount) / 10**6 + parseFloat(item.penalty))* 10000) / 10000).toFixed(4),
      surplusDeficit: 'deficit', // Added to identify deficit items
      is_my_vessel: item?.is_my_vessel,
    }))
  ];

  const handleCopyPoolId = () => {
    copyToClipboard(intiatedpoolingData.pool_id); // Copy Pool ID
  };

  const getRowClassName = (record) => {
    console.log("record",record)
    return record.surplusDeficit == 'surplus' ? 'surplus-row' : 'deficit-row';
  };

  return (
    <div className="pool-details-modal">
      <Modal
        onCancel={onClose}
        open={isVisible}
        width={900}
        centered
        className="pool-details-modal-antd z-1000"
        closable={true} // Adds the default close button (X)
        footer={null}  // Removes OK and Cancel buttons
      >
        <div className="modal-header">
          <Row justify="space-between" align="middle">
            <Col style={{ flex: 1 }}>
              <div>
                <Text strong>Pool Details ({surplusCount+deficitCount} Vessels)</Text>
              </div>
              <div>
                <Tag icon={<CheckCircleOutlined />} color="success">
                  Pool Successful
                </Tag>
              </div>
            </Col>

            <Col style={{ marginRight: '10px' }}>
              <div className={`total-cb-transasction ${intiatedpoolingData.total_cb_transasction >= 0 ? "green" : "red"}`}>
                <img width={30} height={30} src={cbgreenIcon} />
                {intiatedpoolingData.total_cb_transasction >= 0 ? "" + addThousandSeparator(((Math.floor(intiatedpoolingData.total_cb_transasction / 10**6 * 1000000) / 1000000)).toFixed(6)) : addThousandSeparator(((Math.floor(intiatedpoolingData.total_cb_transasction / 10**6 * 1000000) / 1000000)).toFixed(6))} CB
              </div>
            </Col>

            <Col style={{ marginRight: '10px' }}>
              <div className='total-amount'>
                <img width={30} height={30} src={euroCurrencyIcon} />
                {addThousandSeparator(((Math.floor(intiatedpoolingData.settled_price  * 10000) / 10000)).toFixed(4))}
              </div>
            </Col>
          </Row>
        </div>

        <Table
          dataSource={dataSource}
          columns={columns}
          pagination={false}
          className="pool-details-table"
          scroll={{ y: 300 }}
          rowClassName={getRowClassName} // Apply conditional styling to rows
        />

        <div className="modal-footer">
          <div className="pool-id-container">
            <Text>Pool ID: {intiatedpoolingData.pool_id}</Text>
            <button
              style={{ 
                border: 'none', 
                background: 'none', 
                cursor: 'pointer', 
                display: 'flex', 
                alignItems: 'center' 
              }}
              onClick={handleCopyPoolId}
            >
              <CopyOutlined style={{ marginLeft: 8 }} />
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PoolDetailsModal;
